import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { useEffect, useMemo, useState } from 'react';

import { useKioskDeviceGrant } from '@jane/kiosk/data-access';
import type { DeviceGrant } from '@jane/kiosk/data-access/lib/authentication/authentication.types';
import { buildKioskMobileUrl } from '@jane/kiosk/util';

const REFRESH_MARGIN_MS = 15_000;

const MAX_REFRESH_MS = 4 * 60 * 1000;

const RETRY_DELAY_MS = 15_000;

const refreshDelay = (expiresIn: number) =>
  Math.min(expiresIn * 1000 - REFRESH_MARGIN_MS, MAX_REFRESH_MS);

type BankLinkSource = 'Bag' | 'Checkout';

const janeHostedUrl = (storeId: number, source: BankLinkSource) =>
  buildKioskMobileUrl(storeId, 'link-bank', {
    step: 'signed-in-welcome',
    source,
  });

/**
 * Builds the bank link QR url, keeping a device grant alive behind it for stores
 * that authenticate externally. Nothing polls for kiosk sign in: the shopper is
 * already signed in on the kiosk here, only the phone needs a session.
 */
export const useKioskBankLinkQrCode = ({
  enabled = true,
  source,
  storeId,
}: {
  enabled?: boolean;
  source: BankLinkSource;
  storeId: number;
}) => {
  const [deviceGrant, setDeviceGrant] = useState<DeviceGrant | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failedRefreshes, setFailedRefreshes] = useState(0);

  const externalAuthEnabled = useFlag(
    FLAGS.c3EnableKioskQRCodeAuthWithExternalURL
  );
  const usesDeviceGrant = enabled && externalAuthEnabled;

  const { mutate: createDeviceGrant } = useKioskDeviceGrant({
    onSuccess: (grant: DeviceGrant) => {
      if (!usesDeviceGrant) return;
      setDeviceGrant(grant);
      setError(null);
      setFailedRefreshes(0);
    },
    onError: () => {
      if (!usesDeviceGrant) return;
      setError(
        'We could not generate QR code. Close this to keep shopping, or ask a budtender for help linking your bank.'
      );
      setFailedRefreshes((count) => count + 1);
    },
  });

  useEffect(() => {
    if (!usesDeviceGrant) {
      setDeviceGrant(null);
      setError(null);
      setFailedRefreshes(0);
      return;
    }

    setError(null);
    createDeviceGrant();
  }, [usesDeviceGrant]);

  useEffect(() => {
    if (!usesDeviceGrant) return;
    if (failedRefreshes === 0 && !deviceGrant?.external_authentication_url)
      return;

    const timeout = setTimeout(
      () => {
        setError(null);
        createDeviceGrant();
      },
      failedRefreshes > 0
        ? RETRY_DELAY_MS
        : refreshDelay(deviceGrant!.expires_in)
    );

    return () => clearTimeout(timeout);
  }, [usesDeviceGrant, deviceGrant, failedRefreshes]);

  const url = useMemo(() => {
    if (!usesDeviceGrant) return janeHostedUrl(storeId, source);
    if (!deviceGrant) return undefined;
    if (deviceGrant.external_authentication_url == null) {
      return janeHostedUrl(storeId, source);
    }

    const externalUrl = new URL(deviceGrant.external_authentication_url);
    externalUrl.searchParams.set('user_code', deviceGrant.user_code);
    externalUrl.searchParams.set('source', source);

    return externalUrl.toString();
  }, [deviceGrant, source, storeId, usesDeviceGrant]);

  return {
    error: deviceGrant && failedRefreshes < 3 ? null : error,
    url,
  };
};
